/**
 * Users — eMerge (Isaiah Cotto, 2026-10-26): "I need to see our Users in the user tab. I do not want to see anything
 * related to investment, and I want to be able to sign in as."
 *
 * The workforce only (GET /admin/accounts/workforce: employee and admin accounts, never an investor row). Read-only
 * "Sign in as" asks for a reason and the administrator's own authenticator code; the person is told
 * (docs/security/ADMIN_SIGN_IN_AS.md). Actions kept from what already worked for workforce accounts: add an employee
 * (now really an employee, waiting for setup) and send a setup code. Authenticator resets stay on Account Recovery.
 * Deliberately NOT here: role changes (they confer admin with no step-up), delete (hard delete), and anything about
 * investing.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, KeyRound, Mail, Plus, Search, Stethoscope, UserRound } from 'lucide-react';
import { apiRequest } from '../../../services/api';
import { startSignInAs } from '../../../services/impersonation';
import { STEP_UP_SSO_REQUIRED, startSsoStepUp } from '../../../services/ssoApi';
import { Button } from '../../../components/new-components/ui/button';
import { Badge } from '../../../components/new-components/ui/badge';
import { Input } from '../../../components/new-components/ui/input';
import { Textarea } from '../../../components/new-components/ui/textarea';
import { Label } from '../../../components/new-components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/new-components/ui/dialog';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '../../../components/new-components/ui/sheet';

export type WorkforceStatus = 'active' | 'pending' | 'inactive' | 'suspended';

export interface WorkspaceRole { role: string; enterprise: boolean; facilities: number }

export interface WorkforceUser {
  /**
   * True when the server sent the reduced projection (the caller holds SYSTEM_IMPERSONATE, the Sign in as role, and not
   * SYSTEM_USER_MANAGE; ruling 2026-10-29). Then the sign-in security, workspace roles and history fields below are
   * absent and must not be shown.
   */
  limited?: boolean;
  id: string;
  name: string | null;
  email: string;
  role: 'employee' | 'admin';
  status: WorkforceStatus;
  activationState: string;
  isActive: boolean;
  lastSignIn: string | null;
  hasAuthenticator: boolean;
  mfaRequired: boolean;
  ssoLinked: boolean | null;
  isProvider: boolean;
  employee: { id: string; jobTitle: string | null; active: boolean } | null;
  workspaceRoles: WorkspaceRole[];
}

export interface SignInAsRecord {
  id: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  status: 'ACTIVE' | 'EXPIRED' | 'EXITED' | 'REVOKED';
  reason: string;
  administrator: { id: string; name: string; email: string };
  viewed: { id: string; name: string; email: string };
}

export interface WorkforceDetail extends WorkforceUser {
  activationCompletedAt: string | null;
  emailVerifiedAt: string | null;
  canSignInAs: boolean;
  signInAsRefusal: { code: string; message: string } | null;
  signInAs: SignInAsRecord[];
}

const PAGE = 50;

const STATUS_WORD: Record<WorkforceStatus, string> = {
  active: 'Active', pending: 'Setting up', inactive: 'Deactivated', suspended: 'Suspended',
};
const STATUS_STYLE: Record<WorkforceStatus, React.CSSProperties> = {
  active: { background: 'var(--tide-brand-soft, #e4f1ee)', color: 'var(--tide-brand, #17685d)' },
  pending: { background: '#fff4e0', color: '#7a4a00' },
  inactive: { background: 'var(--tide-surface-2, #e9f2f0)', color: 'var(--tide-ink-2, #4f646b)' },
  suspended: { background: '#fde8e4', color: '#9b2c1a' },
};
const RECORD_WORD: Record<SignInAsRecord['status'], string> = {
  ACTIVE: 'In progress', EXPIRED: 'Ran out', EXITED: 'Exited', REVOKED: 'Ended by the server',
};

const WHEN = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});
export function when(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? `${WHEN.format(d)} ET` : '—';
}

/** "PATIENT_ADVOCATE" -> "Patient advocate". */
export function roleWords(code: string): string {
  const s = String(code || '').toLowerCase().replace(/_/g, ' ').trim();
  return s ? s[0].toUpperCase() + s.slice(1) : '';
}

export function scopeWords(r: WorkspaceRole): string {
  if (r.enterprise) return 'all buildings';
  return `${r.facilities} building${r.facilities === 1 ? '' : 's'}`;
}

export function signInMethod(u: Pick<WorkforceUser, 'ssoLinked' | 'hasAuthenticator' | 'status'>): string {
  if (u.ssoLinked) return 'Microsoft 365';
  if (u.hasAuthenticator) return 'Password + authenticator';
  if (u.status === 'pending') return 'Not set up yet';
  return 'Password only';
}

const accountWord = (role: string) => (role === 'admin' ? 'Admin' : 'Employee');
const firstName = (u: { name: string | null; email: string }) => (u.name || u.email).split(' ')[0];

function StatusBadge({ status }: { status: WorkforceStatus }) {
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium" style={STATUS_STYLE[status]}>
      {STATUS_WORD[status]}
    </span>
  );
}

/* --------------------------------------------------------------------------------------------------------- */

/**
 * The reason, kept across the Microsoft step-up round trip (sessionStorage, this tab only, removed when read). It is
 * the administrator's own words for the notice, not a credential.
 */
const DRAFT_KEY = 'emerge.signInAs.draft';
export function saveSignInAsDraft(userId: string, reason: string) {
  try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ userId, reason })); } catch { /* storage unavailable */ }
}
export function takeSignInAsDraft(userId: string): string | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    sessionStorage.removeItem(DRAFT_KEY);
    const d = raw ? JSON.parse(raw) : null;
    return d && d.userId === userId && typeof d.reason === 'string' ? d.reason : null;
  } catch {
    return null;
  }
}
export const signInAsReturnPath = (userId: string) => `/admin/users?signInAs=${encodeURIComponent(userId)}`;

export function SignInAsDialog({ user, open, onOpenChange, initialReason = null, microsoftConfirmed = false }: {
  user: Pick<WorkforceUser, 'id' | 'name' | 'email' | 'role'> | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** After the Microsoft step-up came back: the reason typed before it, and that the proof is waiting. */
  initialReason?: string | null;
  microsoftConfirmed?: boolean;
}) {
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsMicrosoft, setNeedsMicrosoft] = useState(false);

  useEffect(() => {
    if (open) { setReason(initialReason || ''); setCode(''); setError(null); setBusy(false); setNeedsMicrosoft(false); }
  }, [open, user?.id, initialReason]);

  if (!user) return null;
  const name = user.name || user.email;
  const reasonOk = reason.trim().length >= 8 && reason.trim().length <= 500;
  // The code is optional: an administrator who confirms with Microsoft (services/identity/impersonationStepUp.js)
  // leaves it empty, and the server says which it needs.
  const ok = reasonOk && (code.length === 0 || code.length === 6) && !busy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    setError(null);
    setNeedsMicrosoft(false);
    const r = await startSignInAs(user.id, reason.trim(), code);
    if (r.ok) {
      // The page now holds only the read-only token: reload into the person's portal.
      window.location.assign('/');
      return;
    }
    setBusy(false);
    setCode('');
    if (r.code === STEP_UP_SSO_REQUIRED) setNeedsMicrosoft(true);
    setError(r.message || 'Sign in as was refused.');
  };

  const confirmWithMicrosoft = async () => {
    if (!reasonOk) { setError('Write the reason first: it is kept while you confirm with Microsoft.'); return; }
    saveSignInAsDraft(user.id, reason.trim());
    setBusy(true);
    const leaving = await startSsoStepUp('IMPERSONATE', signInAsReturnPath(user.id));
    if (!leaving) setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={submit} aria-label={`Sign in as ${name}`}>
          <DialogHeader>
            <DialogTitle>Sign in as {name}</DialogTitle>
            <DialogDescription>
              {user.email} · {accountWord(user.role)}
            </DialogDescription>
          </DialogHeader>
          <ul className="my-4 space-y-2 text-sm" style={{ color: 'var(--tide-ink, #10252d)' }}>
            <li><strong>Read-only.</strong> You see exactly what {firstName(user)} sees. Nothing can be saved, sent, approved, uploaded, signed or downloaded.</li>
            <li><strong>{firstName(user)} is told.</strong> They get a notice naming you and your reason, and it stays on their account activity.</li>
            <li>It ends after 20 minutes, or when you press Exit. Your own session resumes then.</li>
          </ul>
          <div className="space-y-1.5">
            <Label htmlFor="sia-reason">Reason (they will read it)</Label>
            <Textarea
              id="sia-reason"
              value={reason}
              maxLength={500}
              required
              rows={3}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Checking what Pat sees on the UMED board after the 10/24 change"
            />
            <p className="text-xs" style={{ color: 'var(--tide-ink-3, #586c73)' }}>
              {reason.trim().length < 8 ? `At least 8 characters (${reason.trim().length}/8)` : `${reason.trim().length}/500`}
            </p>
          </div>
          {microsoftConfirmed && (
            <p role="status" className="mt-3 rounded-md px-3 py-2 text-sm" style={{ background: 'var(--tide-brand-soft, #e4f1ee)', color: 'var(--tide-brand, #17685d)' }}>
              Back from Microsoft. If it confirmed it is you, press Sign in as to continue (leave the code empty).
            </p>
          )}
          <div className="mt-3 space-y-1.5">
            <Label htmlFor="sia-code">Your authenticator code (leave empty if you confirm with Microsoft)</Label>
            <Input
              id="sia-code"
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="font-mono tracking-widest"
            />
          </div>
          {error && <p role="alert" className="mt-3 text-sm font-medium" style={{ color: '#9b2c1a' }}>{error}</p>}
          <DialogFooter className="mt-5 gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {needsMicrosoft && (
              <Button type="button" variant="outline" onClick={() => void confirmWithMicrosoft()} disabled={busy}>
                Confirm with Microsoft
              </Button>
            )}
            <Button type="submit" disabled={!ok}>
              <Eye aria-hidden="true" /> {busy ? 'Starting…' : 'Sign in as'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------------------------------------------------------------- */

function AddEmployeeDialog({ open, onOpenChange, onCreated }: {
  open: boolean; onOpenChange: (o: boolean) => void; onCreated: (id: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [role, setRole] = useState<'employee' | 'admin'>('employee');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setEmail(''); setFirst(''); setLast(''); setRole('employee'); setError(null); setBusy(false); } }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await apiRequest<{ id: string }>('POST', '/admin/users', { email: email.trim(), firstName: first.trim(), lastName: last.trim(), role });
    setBusy(false);
    if (r.success && r.data) { onCreated((r.data as { id: string }).id); onOpenChange(false); return; }
    setError(r.message || 'The account was not created.');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} aria-label="Add employee">
          <DialogHeader>
            <DialogTitle>Add employee</DialogTitle>
            <DialogDescription>The account waits for setup. Send the setup code from their row when they are ready.</DialogDescription>
          </DialogHeader>
          <div className="mt-4 grid gap-3">
            <div className="space-y-1.5"><Label htmlFor="ae-email">Work email</Label>
              <Input id="ae-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label htmlFor="ae-first">First name</Label>
                <Input id="ae-first" value={first} onChange={(e) => setFirst(e.target.value)} /></div>
              <div className="space-y-1.5"><Label htmlFor="ae-last">Last name</Label>
                <Input id="ae-last" value={last} onChange={(e) => setLast(e.target.value)} /></div>
            </div>
            <div className="space-y-1.5"><Label htmlFor="ae-role">Account</Label>
              <select id="ae-role" value={role} onChange={(e) => setRole(e.target.value as 'employee' | 'admin')}
                className="h-9 w-full rounded-md border px-2 text-sm" style={{ borderColor: 'var(--tide-boundary, #6b8187)' }}>
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
              </select></div>
          </div>
          {error && <p role="alert" className="mt-3 text-sm font-medium" style={{ color: '#9b2c1a' }}>{error}</p>}
          <DialogFooter className="mt-5 gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !email.trim()}><Plus aria-hidden="true" /> {busy ? 'Adding…' : 'Add'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------------------------------------------------------------- */

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[8.5rem_1fr] gap-2 py-1.5 text-sm">
      <dt style={{ color: 'var(--tide-ink-3, #586c73)' }}>{label}</dt>
      <dd className="min-w-0 break-words" style={{ color: 'var(--tide-ink, #10252d)' }}>{children}</dd>
    </div>
  );
}

function UserDrawer({ userId, you, onClose, onSignInAs }: {
  userId: string | null; you: string | null; onClose: () => void; onSignInAs: (u: WorkforceDetail) => void;
}) {
  const [d, setD] = useState<WorkforceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setD(null); setError(null); setNotice(null);
    if (!userId) return undefined;
    apiRequest<WorkforceDetail>('GET', `/admin/accounts/workforce/${encodeURIComponent(userId)}`).then((r) => {
      if (!live) return;
      if (r.success && r.data) setD(r.data as WorkforceDetail);
      else setError(r.message || 'This account could not be loaded.');
    });
    return () => { live = false; };
  }, [userId]);

  const sendCode = async () => {
    if (!d) return;
    setNotice(null);
    const r = await apiRequest<{ email: string; delivered: boolean }>('POST', `/admin/accounts/${encodeURIComponent(d.id)}/send-code`, {});
    const data = r.data as { email: string; delivered: boolean } | undefined;
    if (r.success && data) setNotice(data.delivered ? `Setup code sent to ${data.email}.` : `A code was issued but the email did not go out. Deliver it another way.`);
    else setNotice(r.message || 'The setup code was not sent.');
  };

  return (
    <Sheet open={Boolean(userId)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{d ? d.name || d.email : 'Account'}</SheetTitle>
          <SheetDescription>{d ? `${d.email} · ${accountWord(d.role)}` : error ? '' : 'Loading…'}</SheetDescription>
        </SheetHeader>
        {error && <p role="alert" className="px-4 text-sm" style={{ color: '#9b2c1a' }}>{error}</p>}
        {d && (
          <div className="space-y-5 px-4 pb-6">
            <dl>
              <Detail label="Status"><StatusBadge status={d.status} /></Detail>
              {!d.limited && <Detail label="Last sign-in">{when(d.lastSignIn)}</Detail>}
              {!d.limited && <Detail label="Sign-in method">{signInMethod(d)}</Detail>}
              {!d.limited && <Detail label="Authenticator">{d.hasAuthenticator ? 'Enrolled' : 'None'}{d.mfaRequired ? ' (required)' : ''}</Detail>}
              <Detail label="Employee record">{d.employee ? `${d.employee.jobTitle || 'No job title'}${d.employee.active ? '' : ' (not on the active roster)'}` : 'Not linked'}</Detail>
              <Detail label="Provider">{d.isProvider ? 'Yes' : 'No'}</Detail>
              {!d.limited && <Detail label="Set up">{d.activationCompletedAt ? when(d.activationCompletedAt) : 'Not finished'}</Detail>}
            </dl>

            {!d.limited && (
            <section aria-labelledby="ws-h">
              <h3 id="ws-h" className="mb-1.5 text-sm font-semibold">Workspaces</h3>
              {d.workspaceRoles.length === 0
                ? <p className="text-sm" style={{ color: 'var(--tide-ink-3, #586c73)' }}>No ratified workspace role.</p>
                : (
                  <ul className="flex flex-wrap gap-1.5">
                    {d.workspaceRoles.map((r) => (
                      <li key={r.role}><Badge variant="outline">{roleWords(r.role)} · {scopeWords(r)}</Badge></li>
                    ))}
                  </ul>
                )}
            </section>
            )}

            <section aria-labelledby="act-h" className="space-y-2">
              <h3 id="act-h" className="text-sm font-semibold">Actions</h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => onSignInAs(d)}
                  disabled={!d.canSignInAs}
                  aria-describedby={d.canSignInAs ? undefined : 'sia-why'}
                >
                  <Eye aria-hidden="true" /> Sign in as…
                </Button>
                {!d.limited && d.status === 'pending' && (
                  <Button type="button" variant="outline" onClick={() => void sendCode()}><Mail aria-hidden="true" /> Send setup code</Button>
                )}
                {!d.limited && d.id !== you && (
                  <Button type="button" variant="outline" asChild>
                    <Link to="/admin/emerge/account-recovery"><KeyRound aria-hidden="true" /> Account recovery</Link>
                  </Button>
                )}
              </div>
              {!d.canSignInAs && d.signInAsRefusal && (
                <p id="sia-why" className="text-xs" style={{ color: 'var(--tide-ink-3, #586c73)' }}>{d.signInAsRefusal.message}</p>
              )}
              {notice && <p role="status" className="text-sm">{notice}</p>}
            </section>

            {!d.limited && (
            <section aria-labelledby="hist-h">
              <h3 id="hist-h" className="mb-1.5 text-sm font-semibold">Sign-in-as history</h3>
              {d.signInAs.length === 0
                ? <p className="text-sm" style={{ color: 'var(--tide-ink-3, #586c73)' }}>Nobody has signed in as this person, and they have not signed in as anyone.</p>
                : (
                  <ol className="space-y-2">
                    {d.signInAs.map((h) => (
                      <li key={h.id} className="rounded-md border p-2 text-sm" style={{ borderColor: 'var(--tide-line, #d7e5e1)' }}>
                        <div className="font-medium">
                          {h.viewed.id === d.id ? `${h.administrator.name} viewed as them` : `Viewed as ${h.viewed.name}`}
                        </div>
                        <div style={{ color: 'var(--tide-ink-2, #4f646b)' }}>{when(h.startedAt)} · {RECORD_WORD[h.status]}</div>
                        <div className="mt-1 break-words">“{h.reason}”</div>
                      </li>
                    ))}
                  </ol>
                )}
            </section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* --------------------------------------------------------------------------------------------------------- */

interface ListData { you: string; items: WorkforceUser[]; total: number; limit: number; offset: number; limited?: boolean }

export default function WorkforceUsers() {
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<ListData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [signInAs, setSignInAs] = useState<WorkforceDetail | null>(null);
  const [returned, setReturned] = useState<{ reason: string | null } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const t = window.setTimeout(() => { setTerm(search.trim()); setOffset(0); }, 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (term) p.set('search', term);
    if (role) p.set('role', role);
    if (status) p.set('status', status);
    p.set('limit', String(PAGE));
    p.set('offset', String(offset));
    return p.toString();
  }, [term, role, status, offset]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    const r = await apiRequest<ListData>('GET', `/admin/accounts/workforce?${query}`);
    if (mine !== seq.current) return;
    setLoading(false);
    if (r.success && r.data) { setData(r.data as ListData); setError(null); return; }
    setError(r.status === 403
      ? 'Seeing users needs the system administrator role (SYSTEM_USER_MANAGE) or the Sign in as role (SYSTEM_IMPERSONATE).'
      : r.status === undefined ? 'The server could not be reached.' : r.message || 'Users could not be loaded.');
  }, [query]);

  useEffect(() => { void load(); }, [load]);

  // Back from the Microsoft step-up (/admin/users?signInAs=<id>): reopen Sign in as for that person, with the reason
  // typed before leaving. The query is dropped at once so a reload does not reopen it.
  const mounted = useRef(true);
  const handledReturn = useRef<string | null>(null);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('signInAs');
    if (!id || !/^[0-9a-f-]{36}$/i.test(id) || handledReturn.current === id) return;
    handledReturn.current = id; // once per return, however often the router re-renders before the query is gone
    navigate('/admin/users', { replace: true }); // re-runs this effect with no id: nothing to cancel
    const reason = takeSignInAsDraft(id);
    void apiRequest<WorkforceDetail>('GET', `/admin/accounts/workforce/${encodeURIComponent(id)}`).then((r) => {
      if (!mounted.current || !r.success || !r.data) return;
      setReturned({ reason });
      setSignInAs(r.data as WorkforceDetail);
    });
  }, [location.search, navigate]);

  const items = data?.items || [];
  const limited = Boolean(data?.limited);
  const total = data?.total || 0;
  const from = total ? offset + 1 : 0;
  const to = Math.min(offset + items.length, total);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6" style={{ color: 'var(--tide-ink, #10252d)' }}>
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-sm" style={{ color: 'var(--tide-ink-2, #4f646b)' }}>Everyone who signs in to eMerge: employees and admins.</p>
        </div>
        {!limited && <Button type="button" onClick={() => setAdding(true)}><Plus aria-hidden="true" /> Add employee</Button>}
      </header>

      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]" role="search">
        <label className="relative block">
          <span className="sr-only">Search by name or email</span>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2" aria-hidden="true" style={{ color: 'var(--tide-ink-3, #586c73)' }} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" className="pl-8" maxLength={100} />
        </label>
        <label className="block">
          <span className="sr-only">Role</span>
          <select value={role} onChange={(e) => { setRole(e.target.value); setOffset(0); }} aria-label="Role"
            className="h-9 w-full rounded-md border bg-white px-2 text-sm" style={{ borderColor: 'var(--tide-boundary, #6b8187)' }}>
            <option value="">All roles</option>
            <option value="employee">Employees</option>
            <option value="admin">Admins</option>
          </select>
        </label>
        <label className="block">
          <span className="sr-only">Status</span>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} aria-label="Status"
            className="h-9 w-full rounded-md border bg-white px-2 text-sm" style={{ borderColor: 'var(--tide-boundary, #6b8187)' }}>
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="pending">Setting up</option>
            <option value="inactive">Deactivated</option>
            <option value="suspended">Suspended</option>
          </select>
        </label>
      </div>

      {error && <p role="alert" className="mb-3 rounded-md border px-3 py-2 text-sm" style={{ borderColor: '#e5b4aa', background: '#fdf1ee', color: '#9b2c1a' }}>{error}</p>}

      <div className="overflow-hidden rounded-lg border bg-white" style={{ borderColor: 'var(--tide-line, #d7e5e1)' }}>
        <table className="w-full table-fixed text-left text-sm">
          <caption className="sr-only">Workforce accounts</caption>
          <thead style={{ background: 'var(--tide-surface-2, #e9f2f0)', color: 'var(--tide-ink-2, #4f646b)' }}>
            <tr>
              <th scope="col" className="w-[45%] px-3 py-2 font-medium sm:w-[26%]">Name</th>
              <th scope="col" className="hidden px-3 py-2 font-medium md:table-cell md:w-[22%]">Email</th>
              <th scope="col" className="hidden px-3 py-2 font-medium sm:table-cell sm:w-[10%]">Role</th>
              {!limited && <th scope="col" className="hidden px-3 py-2 font-medium lg:table-cell lg:w-[16%]">Workspaces</th>}
              <th scope="col" className="w-[25%] px-3 py-2 font-medium sm:w-[12%]">Status</th>
              {!limited && <th scope="col" className="hidden px-3 py-2 font-medium md:table-cell md:w-[14%]">Last sign-in</th>}
              {!limited && <th scope="col" className="hidden px-3 py-2 font-medium xl:table-cell xl:w-[14%]">Sign-in method</th>}
              <th scope="col" className="w-[30%] px-3 py-2 text-right font-medium sm:w-[10%]"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading && !data && (
              <tr><td colSpan={limited ? 5 : 8} className="px-3 py-6 text-center" style={{ color: 'var(--tide-ink-3, #586c73)' }}>Loading…</td></tr>
            )}
            {!loading && items.length === 0 && !error && (
              <tr><td colSpan={limited ? 5 : 8} className="px-3 py-6 text-center" style={{ color: 'var(--tide-ink-3, #586c73)' }}>No one matches.</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.id} className="border-t align-top hover:bg-[var(--tide-interact-soft,#d5f5f0)]" style={{ borderColor: 'var(--tide-line, #d7e5e1)' }}>
                <td className="px-3 py-2">
                  <button type="button" onClick={() => setOpenId(u.id)} className="block w-full min-w-0 text-left font-medium underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2" style={{ outlineColor: 'var(--tide-focus, #0c8577)' }}>
                    <span className="flex items-center gap-1.5">
                      <UserRound className="h-4 w-4 flex-shrink-0" aria-hidden="true" style={{ color: 'var(--tide-ink-3, #586c73)' }} />
                      <span className="truncate">{u.name || u.email}</span>
                      {u.isProvider && <span title="Provider"><Stethoscope className="h-3.5 w-3.5 flex-shrink-0" aria-label="Provider" style={{ color: 'var(--tide-cat-provider, #232a7a)' }} /></span>}
                    </span>
                    <span className="block truncate text-xs font-normal md:hidden" style={{ color: 'var(--tide-ink-2, #4f646b)' }}>{u.email}</span>
                  </button>
                </td>
                <td className="hidden truncate px-3 py-2 md:table-cell" title={u.email}>{u.email}</td>
                <td className="hidden px-3 py-2 sm:table-cell">{accountWord(u.role)}</td>
                {!limited && (
                  <td className="hidden px-3 py-2 lg:table-cell">
                    {u.workspaceRoles.length === 0 ? <span style={{ color: 'var(--tide-ink-3, #586c73)' }}>None</span>
                      : <span className="line-clamp-2">{u.workspaceRoles.map((r) => roleWords(r.role)).join(', ')}</span>}
                  </td>
                )}
                <td className="px-3 py-2"><StatusBadge status={u.status} /></td>
                {!limited && <td className="hidden px-3 py-2 md:table-cell">{when(u.lastSignIn)}</td>}
                {!limited && <td className="hidden px-3 py-2 xl:table-cell">{signInMethod(u)}</td>}
                <td className="px-3 py-2 text-right">
                  <Button type="button" size="sm" variant="outline" onClick={() => setOpenId(u.id)} aria-label={`Open ${u.name || u.email}`}>Open</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <nav className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm" aria-label="Pages">
        <span style={{ color: 'var(--tide-ink-2, #4f646b)' }}>{total ? `${from}–${to} of ${total}` : ''}</span>
        <span className="flex gap-2">
          <Button type="button" variant="outline" size="sm" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
          <Button type="button" variant="outline" size="sm" disabled={offset + PAGE >= total || loading} onClick={() => setOffset(offset + PAGE)}>Next</Button>
        </span>
      </nav>

      <UserDrawer userId={openId} you={data?.you || null} onClose={() => setOpenId(null)} onSignInAs={(u) => { setOpenId(null); setSignInAs(u); }} />
      <SignInAsDialog
        user={signInAs}
        open={Boolean(signInAs)}
        onOpenChange={(o) => { if (!o) { setSignInAs(null); setReturned(null); } }}
        initialReason={returned ? returned.reason : null}
        microsoftConfirmed={Boolean(returned)}
      />
      <AddEmployeeDialog open={adding} onOpenChange={setAdding} onCreated={(id) => { void load(); setOpenId(id); }} />
    </div>
  );
}
